import { equals } from 'ramda';
import { ChartDateRange, DimensionId } from '@blisspointmedia/bpm-types/dist/dashboarding';
import { FilterNode, isFilterLeaf } from '@blisspointmedia/bpm-types/dist/querying';
import {
  ChartViewParams,
  fromChartViewSearch,
  PARAM,
  toChartViewSearch
} from '../Charts/chart-view-params';
import { isFilterNodeList, MAX_DASHBOARD_FILTERS } from './dashboard-filter-utils';

// The dashboard's URL contract: the same transient view state chart-view-params.ts carries for a
// single focused chart, extended to cover a whole dashboard -- a GLOBAL date window that applies to
// every card, plus per-card OVERRIDES of that same shape. A shared dashboard link therefore carries
// exactly what the dashboard was showing: concrete dates (never a picker preset -- the rolling-preset
// behaviour lives on the saved dashboard's defaultDateConfig, not in the URL) and only the cards whose
// view actually diverges from the global window.
export interface DashboardViewParams {
  globalDateRange?: ChartDateRange;
  globalComparisonRange?: ChartDateRange;
  /** Sparse: only charts with at least one override. */
  perChart: Record<string, ChartViewParams>;
  /** undefined = same as the saved default; [] = explicitly no filters. */
  dashboardFilters?: FilterNode[];
  /** Read only: the URL had filter params that could not be read, so the saved default is used. */
  dashboardFiltersRejected?: boolean;
}

// 'f.<dim>=v' is an `in` leaf, 'fx.<dim>=v' a `notIn` leaf; 'filters=' (base64url JSON) wins when set.
const DASHBOARD_FILTER_PARAM = { IN: 'f.', NOT_IN: 'fx.', TREE: 'filters' } as const;

// The focused chart view also carries the opt-outs of the card it was expanded from (repeated
// 'ignoreFilters=<dimensionId>'). A dashboard URL never carries them: there they live on each card.
export const FOCUSED_VIEW_FILTER_PARAM = { IGNORE: 'ignoreFilters' } as const;

const SIMPLE_OPS = { in: DASHBOARD_FILTER_PARAM.IN, notIn: DASHBOARD_FILTER_PARAM.NOT_IN } as const;

// Per-chart values are written as '<chartId>~<value>' under the SAME keys chart-view-params.ts uses
// for the global window ('start', 'kpi', etc.) -- there's no separate per-chart param namespace.
//
// '~' is NOT chosen for URL readability: URLSearchParams.toString() form-urlencodes everything
// outside [A-Za-z0-9*-._], so on the wire a chart id reads 'chart%3A<uuid>%7E<value>'. It's chosen
// because it cannot occur in a chart id, which is what makes the first-separator split below
// unambiguous for any user-authored value.
const CHART_PREFIX_SEP = '~';

// Split off a chart id at the FIRST separator only, so the rest of the raw value -- which is
// user-authored and can legitimately contain '~' itself (a custom-segment dimension id, or the colId
// half of an OutputFieldRef, e.g. 'q0.Brand.Generic') -- round-trips intact. A chart id is
// 'chart:' || gen_random_uuid() and can never contain '~', so the first occurrence always belongs to
// the boundary. Same reasoning as parseFieldRef splitting on the first '.'
// (bpm-types/src/querying/ids.ts:72-80). Returns undefined for a bare (unprefixed) value.
const splitChartPrefix = (raw: string): { chartId: string; value: string } | undefined => {
  const sepIndex = raw.indexOf(CHART_PREFIX_SEP);
  if (sepIndex === -1) return undefined;
  return { chartId: raw.slice(0, sepIndex), value: raw.slice(sepIndex + 1) };
};

// base64url over UTF-8 bytes (no padding), so non-ASCII filter values survive.
const encodeBase64Url = (text: string): string => {
  const binary = Array.from(new TextEncoder().encode(text), byte => String.fromCharCode(byte)).join(
    ''
  );
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
};

const decodeBase64Url = (encoded: string): string => {
  const binary = atob(encoded.replace(/-/g, '+').replace(/_/g, '/'));
  return new TextDecoder('utf-8', { fatal: true }).decode(
    Uint8Array.from(binary, char => char.charCodeAt(0))
  );
};

// Unlike a URL with too many filters (ignored whole), a set list is clamped: past the cap the server's
// getData would 400 every card's request (MAX_DASHBOARD_FILTERS in bpm-lambdas).
export const clampDashboardFilters = (filters: FilterNode[]): FilterNode[] =>
  filters.length > MAX_DASHBOARD_FILTERS ? filters.slice(0, MAX_DASHBOARD_FILTERS) : filters;

const parseFilterTree = (raw: string): FilterNode[] | undefined => {
  try {
    const parsed: unknown = JSON.parse(decodeBase64Url(raw));
    return isFilterNodeList(parsed) ? parsed : undefined;
  } catch {
    return undefined;
  }
};

// The simple params can only say "dimension is one of / none of these strings". Anything else, or two
// leaves sharing a dimension and op (which would merge into one), has to go through 'filters='.
const toSimpleFilterSearch = (filters: FilterNode[]): [string, string][] | undefined => {
  const seen = new Set<string>();
  const entries: [string, string][] = [];
  for (const node of filters) {
    // A malformed node (kept so the server can report it) goes through 'filters=' untouched.
    if (
      !isFilterLeaf(node) ||
      typeof node.dimension?.id !== 'string' ||
      !Array.isArray(node.values)
    ) {
      return undefined;
    }
    const prefix = node.op === 'in' || node.op === 'notIn' ? SIMPLE_OPS[node.op] : undefined;
    const key = `${prefix}${node.dimension.id}`;
    const values = node.values.filter((value): value is string => typeof value === 'string');
    if (
      !prefix ||
      node.dimension.id === '' ||
      values.length === 0 ||
      values.length !== node.values.length ||
      values.includes('') ||
      seen.has(key)
    ) {
      return undefined;
    }
    seen.add(key);
    values.forEach(value => entries.push([key, value]));
  }
  return entries;
};

const writeFilterParams = (search: URLSearchParams, filters: FilterNode[]) => {
  const simple = filters.length > 0 ? toSimpleFilterSearch(filters) : undefined;
  if (simple) {
    simple.forEach(([key, value]) => search.append(key, value));
    return;
  }
  search.set(DASHBOARD_FILTER_PARAM.TREE, encodeBase64Url(JSON.stringify(filters)));
};

const filterParamPrefix = (key: string): string | undefined =>
  [DASHBOARD_FILTER_PARAM.NOT_IN, DASHBOARD_FILTER_PARAM.IN].find(prefix => key.startsWith(prefix));

const isFilterParamKey = (key: string): boolean =>
  key === DASHBOARD_FILTER_PARAM.TREE || filterParamPrefix(key) !== undefined;

// undefined when the URL carries no filter params at all (use the saved default).
const readFilterParams = (search: URLSearchParams): FilterNode[] | undefined => {
  const tree = search.get(DASHBOARD_FILTER_PARAM.TREE);
  if (tree !== null) return parseFilterTree(tree);

  let hasFilterParams = false;
  const byKey = new Map<string, string[]>();
  search.forEach((value, key) => {
    const prefix = filterParamPrefix(key);
    if (prefix === undefined) return;
    hasFilterParams = true;
    const dimensionId = key.slice(prefix.length);
    // An empty value (or dimension) only marks "remove the default"; it adds no leaf.
    if (value === '' || dimensionId === '') return;
    byKey.set(key, [...(byKey.get(key) ?? []), value]);
  });
  if (!hasFilterParams) return undefined;

  // Unlike the setter (which clamps), a URL past the server's 400 limit (bpm-lambdas getData) is
  // ignored whole, so a link never shows a silently truncated filter set.
  if (byKey.size > MAX_DASHBOARD_FILTERS) return undefined;

  return Array.from(byKey, ([key, values]): FilterNode => {
    const isNotIn = key.startsWith(DASHBOARD_FILTER_PARAM.NOT_IN);
    const prefix = isNotIn ? DASHBOARD_FILTER_PARAM.NOT_IN : DASHBOARD_FILTER_PARAM.IN;
    return { dimension: { id: key.slice(prefix.length) }, op: isNotIn ? 'notIn' : 'in', values };
  });
};

// Serialize to a search string ("?start=…&start=chart%3A…~2026-08-01…", or "" when there's nothing to
// carry) for navigate(). Delegates every value's actual formatting to toChartViewSearch -- this file
// only ever adds or strips the 'chartId~' prefix; it never re-implements date writing or validation.
export const toDashboardViewSearch = (view: DashboardViewParams): string => {
  const search = new URLSearchParams();

  // Global window: bare params, exactly as a focused chart view would write them.
  const globalSearch = new URLSearchParams(
    toChartViewSearch({
      dateRange: view.globalDateRange,
      comparisonRange: view.globalComparisonRange
    }).replace(/^\?/, '')
  );
  globalSearch.forEach((value, key) => search.append(key, value));

  // Per-chart overrides: same keys, `chartId~value`. Repeated keys (append, not set) let multiple
  // charts each carry their own override of the same param side by side.
  Object.entries(view.perChart).forEach(([chartId, chartParams]) => {
    const chartSearch = new URLSearchParams(toChartViewSearch(chartParams).replace(/^\?/, ''));
    chartSearch.forEach((value, key) => {
      search.append(key, `${chartId}${CHART_PREFIX_SEP}${value}`);
    });
  });

  if (view.dashboardFilters) {
    writeFilterParams(search, view.dashboardFilters);
  }

  const query = search.toString();
  return query ? `?${query}` : '';
};

// Read whatever the URL carries. Every key's repeated values are partitioned into bare (the global
// window) vs chartId-prefixed (a card's override), then each side is handed to fromChartViewSearch so
// all date parsing/validation comes from that one module. `kpi`/`metric`/`breakdown` have no global
// slot -- a bare one is malformed and is simply never read back out of the global side below, which
// is what "ignore it" amounts to here. A partial or hand-edited query yields whatever parses and
// undefined/absent for the rest; this never throws.
export const fromDashboardViewSearch = (search: URLSearchParams): DashboardViewParams => {
  const globalSearch = new URLSearchParams();
  const perChartSearch = new Map<string, URLSearchParams>();

  for (const key of Object.values(PARAM)) {
    for (const raw of search.getAll(key)) {
      const split = splitChartPrefix(raw);
      if (!split) {
        // First bare value wins; a hand-edited duplicate shouldn't clobber it.
        if (!globalSearch.has(key)) {
          globalSearch.set(key, raw);
        }
        continue;
      }
      const chartSearch = perChartSearch.get(split.chartId) ?? new URLSearchParams();
      if (!chartSearch.has(key)) {
        chartSearch.set(key, split.value);
      }
      perChartSearch.set(split.chartId, chartSearch);
    }
  }

  const { dateRange: globalDateRange, comparisonRange: globalComparisonRange } =
    fromChartViewSearch(globalSearch);

  const perChart: Record<string, ChartViewParams> = {};
  perChartSearch.forEach((chartSearch, chartId) => {
    const chartViewParams = fromChartViewSearch(chartSearch);
    // Sparse: a chart whose only prefixed params failed to parse (e.g. a start with no matching end)
    // carries no real override, so it gets no entry at all.
    if (Object.values(chartViewParams).some(value => value !== undefined)) {
      perChart[chartId] = chartViewParams;
    }
  });

  const dashboardFilters = readFilterParams(search);
  const rejected =
    dashboardFilters === undefined && Array.from(search.keys()).some(isFilterParamKey);
  return {
    globalDateRange,
    globalComparisonRange,
    perChart,
    dashboardFilters,
    ...(rejected && { dashboardFiltersRejected: true })
  };
};

// An explicit URL beats the saved default; `undefined` means the URL said nothing, `[]` means none.
export const initialDashboardFilters = (
  fromUrl: FilterNode[] | undefined,
  saved: FilterNode[]
): FilterNode[] => fromUrl ?? saved;

// Only a deviation from the saved default goes in the URL. Compared by value: a reference check
// would call an equal tree a deviation.
export const dashboardFiltersForUrl = (
  current: FilterNode[],
  saved: FilterNode[]
): FilterNode[] | undefined => (equals(current, saved) ? undefined : current);

// A preview (an unsaved agent change) is shown but kept out of the URL and out of `withoutPreview`,
// so neither a reload nor a link that leaves the dashboard can keep it.
export const effectiveDashboardFilters = ({
  state,
  preview,
  saved
}: {
  state: FilterNode[];
  preview: FilterNode[] | undefined;
  saved: FilterNode[];
}): { shown: FilterNode[]; withoutPreview: FilterNode[]; forUrl: FilterNode[] | undefined } => ({
  shown: preview ?? state,
  withoutPreview: state,
  forUrl: dashboardFiltersForUrl(state, saved)
});

// A link whose filter params could not be read opens on the saved filters. Say so only while the
// view still shows those, unpreviewed, and they are still the saved ones.
export const linkFiltersStillRejected = ({
  rejected,
  openedSaved,
  state,
  preview,
  saved
}: {
  rejected: boolean;
  openedSaved: FilterNode[];
  state: FilterNode[];
  preview: FilterNode[] | undefined;
  saved: FilterNode[];
}): boolean =>
  rejected && preview === undefined && equals(state, openedSaved) && equals(saved, openedSaved);

// Merge a change into the CURRENT params, dropping every key this module owns before writing, so
// clearing an override (or the whole dashboard view) removes its params rather than stranding them --
// mirrors withChartViewParams (chart-view-params.ts:93-96). Unrelated params on `current` are left
// untouched. Returns a URLSearchParams for setSearchParams.
export const withDashboardViewParams = (
  current: URLSearchParams,
  view: DashboardViewParams
): URLSearchParams => {
  const next = new URLSearchParams(current);
  for (const key of Object.values(PARAM)) {
    next.delete(key);
  }
  Array.from(next.keys())
    .filter(isFilterParamKey)
    .forEach(key => next.delete(key));
  const written = new URLSearchParams(toDashboardViewSearch(view).replace(/^\?/, ''));
  written.forEach((value, key) => next.append(key, value));
  return next;
};

// The dashboard filters a focused chart view was opened with: the dashboard's filters plus the
// opt-outs of the card it came from. Both undefined for a chart opened cold.
export interface FocusedViewFilters {
  dashboardFilters?: FilterNode[];
  ignoreDashboardFilters?: DimensionId[];
}

// The focused view's search string: the chart view params plus the card's dashboard filters. With no
// filters the opt-outs have nothing to opt out of, so they are dropped too.
export const toFocusedViewSearch = (
  view: ChartViewParams,
  { dashboardFilters, ignoreDashboardFilters }: FocusedViewFilters
): string => {
  const search = new URLSearchParams(toChartViewSearch(view).replace(/^\?/, ''));
  if (dashboardFilters?.length) {
    writeFilterParams(search, dashboardFilters);
    ignoreDashboardFilters?.forEach(id => search.append(FOCUSED_VIEW_FILTER_PARAM.IGNORE, id));
  }
  const query = search.toString();
  return query ? `?${query}` : '';
};

// Read back what toFocusedViewSearch wrote. Never throws; a malformed filter param reads as no filters.
export const fromFocusedViewFilterSearch = (search: URLSearchParams): FocusedViewFilters => {
  const ignore = search.getAll(FOCUSED_VIEW_FILTER_PARAM.IGNORE).filter(Boolean);
  return {
    dashboardFilters: readFilterParams(search),
    ignoreDashboardFilters: ignore.length > 0 ? ignore : undefined
  };
};
